import React, {useState, useEffect, useRef, useCallback} from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import TextEllipsis from '../../../components/TextEllipsis/TextEllipsis';

const CYCLE_DURATION = 60000; // 60 seconds total cycle
const MIN_PAGE_INTERVAL = 5000; // Minimum 5 seconds per page
const MEASURE_LIMIT = 50; // Max rows to measure for calculating average height
const PROGRESS_UPDATE_INTERVAL = 500; // Update progress every 500ms (reduced from 100ms for performance)

const Widget = ({title, columns, data, loading, pathField, pathField2, onMaximize}) => {
  const contentRef = useRef(null);
  const theadRef = useRef(null);
  const tbodyRef = useRef(null);
  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [isCalculating, setIsCalculating] = useState(true);
  const [progress, setProgress] = useState(0);
  const [activePathFieldIndex, setActivePathFieldIndex] = useState(0);

  // Measure first N rows to calculate average height, then estimate rows per page
  const calculateRowsPerPage = useCallback(() => {
    if (!contentRef.current || !theadRef.current || !tbodyRef.current || data.length === 0) {
      setRowsPerPage(10);
      setIsCalculating(false);
      return;
    }

    const containerHeight = contentRef.current.clientHeight;
    const theadHeight = theadRef.current.offsetHeight;
    const availableHeight = containerHeight - theadHeight;

    const rows = tbodyRef.current.querySelectorAll('tr');
    if (rows.length === 0) {
      setRowsPerPage(10);
      setIsCalculating(false);
      return;
    }

    // Calculate average row height from measured rows
    let totalHeight = 0;
    rows.forEach(row => {
      totalHeight += row.offsetHeight;
    });
    const avgRowHeight = totalHeight / rows.length;

    // Calculate how many rows fit
    const calculatedRows = Math.floor(availableHeight / avgRowHeight);
    setRowsPerPage(Math.max(1, calculatedRows));
    setIsCalculating(false);
  }, [data]);

  // Trigger calculation after render
  useEffect(() => {
    setIsCalculating(true);
    const timer = setTimeout(calculateRowsPerPage, 50);
    return () => clearTimeout(timer);
  }, [data, calculateRowsPerPage]);

  // Recalculate on resize
  useEffect(() => {
    const handleResize = () => {
      setIsCalculating(true);
      setTimeout(calculateRowsPerPage, 50);
    };
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [calculateRowsPerPage]);

  // Calculate pagination
  const totalPages = Math.ceil(data.length / rowsPerPage) || 1;
  const needsPagination = data.length > rowsPerPage;

  // Auto-cycle through pages with progress bar
  useEffect(() => {
    if (!needsPagination || loading || isCalculating) {
      setCurrentPage(0);
      setProgress(0);
      return;
    }

    const pageInterval = Math.max(CYCLE_DURATION / totalPages, MIN_PAGE_INTERVAL);
    let startTime = Date.now();

    // Reset progress when starting
    setProgress(0);

    // Update progress bar
    const progressTimer = setInterval(() => {
      const elapsed = Date.now() - startTime;
      const newProgress = Math.min((elapsed / pageInterval) * 100, 100);
      setProgress(newProgress);
    }, PROGRESS_UPDATE_INTERVAL);

    // Change page and reset progress start time
    const pageTimer = setInterval(() => {
      setCurrentPage(prev => (prev + 1) % totalPages);
      setProgress(0);
      startTime = Date.now(); // Reset start time for progress calculation
    }, pageInterval);

    return () => {
      clearInterval(progressTimer);
      clearInterval(pageTimer);
    };
  }, [needsPagination, totalPages, loading, isCalculating]); // Removed currentPage - intervals handle page cycling internally

  // Reset to first page when data changes
  useEffect(() => {
    setCurrentPage(0);
  }, [data]);

  // Reset path field index when page changes
  useEffect(() => {
    setActivePathFieldIndex(0);
  }, [currentPage]);

  // Toggle between pathField and pathField2 at half the page interval
  useEffect(() => {
    if (!pathField2 || loading || isCalculating) {
      setActivePathFieldIndex(0);
      return;
    }

    const pageInterval = needsPagination
      ? Math.max(CYCLE_DURATION / totalPages, MIN_PAGE_INTERVAL)
      : CYCLE_DURATION;
    const halfInterval = pageInterval / 2;

    // Toggle between path fields continuously
    const toggleTimer = setInterval(() => {
      setActivePathFieldIndex(prev => (prev === 0 ? 1 : 0));
    }, halfInterval);

    return () => clearInterval(toggleTimer);
  }, [pathField2, loading, isCalculating, needsPagination, totalPages]); // Removed currentPage - separate effect handles reset

  // Get current page data
  const startIndex = currentPage * rowsPerPage;
  const displayData = data.slice(startIndex, startIndex + rowsPerPage);

  // Data for measurement (limited to first N rows)
  const measureData = data.slice(0, MEASURE_LIMIT);

  // Get the currently active path field based on toggle state
  const getActivePathField = () => {
    if (!pathField2) return pathField;
    return activePathFieldIndex === 0 ? pathField : pathField2;
  };
  const activePathField = getActivePathField();

  // Handle maximize button click - call parent callback
  const handleMaximize = (e) => {
    e.stopPropagation();
    if (onMaximize) {
      onMaximize();
    }
  };

  // Handle row click - open storage page if action_id exists
  const handleRowClick = (row) => {
    if (row.action_id) {
      window.open(`/storage?action_id=${row.action_id}`, '_blank');
    }
  };

  // Calculate column widths - use custom width if specified, otherwise distribute remaining space equally
  const getColumnWidth = (col) => {
    if (col.width) {
      return col.width;
    }
    const fixedWidthSum = columns.reduce((sum, c) => sum + (c.width || 0), 0);
    const flexColumns = columns.filter(c => !c.width).length;
    return flexColumns > 0 ? (100 - fixedWidthSum) / flexColumns : 0;
  };

  // Get urgency color based on urgency level
  const getUrgencyColor = (urgency) => {
    switch (urgency) {
      case 0: return '#dc3545'; // 🔴 Overdue
      case 1: return '#fd7e14'; // 🟠 < 6h
      case 2: return '#ffc107'; // 🟡 < 24h
      case 3: return '#6c757d'; // ⚪ > 24h
      default: return null;
    }
  };

  return (
    <WidgetStyled className="widget">
      <WidgetHeader>
        <HeaderLeft>
          <span>{title}</span>
          <MaximizeButton onClick={handleMaximize} title="Maximize">
            <i className="fa fa-expand" />
          </MaximizeButton>
        </HeaderLeft>
        {needsPagination && !isCalculating && (
          <PageIndicatorContainer>
            <PageIndicator>page {currentPage + 1} of {totalPages}</PageIndicator>
            <ProgressBar>
              <ProgressFill $progress={progress} />
            </ProgressBar>
          </PageIndicatorContainer>
        )}
      </WidgetHeader>
      <WidgetContent ref={contentRef}>
        {loading ? (
          <LoadingOverlay>
            <i className="fa fa-spinner fa-spin" />
          </LoadingOverlay>
        ) : null}
        <WidgetTable>
          <thead ref={theadRef}>
            <tr>
              <th>
                <RowColumns>
                  {columns.map(col => (
                    <RowCell key={col.key} $width={getColumnWidth(col)}>{col.label}</RowCell>
                  ))}
                </RowColumns>
              </th>
            </tr>
          </thead>
          <MeasureTbody ref={tbodyRef} $hidden={!isCalculating}>
            {measureData.map((row, idx) => (
              <tr key={idx}>
                <td>
                  <RowContainer>
                    <RowColumns>
                      {columns.map(col => (
                        <RowCell key={col.key} $width={getColumnWidth(col)}>
                          {col.render ? col.render(row[col.key], row) : row[col.key]}
                        </RowCell>
                      ))}
                    </RowColumns>
                    {pathField && row[pathField] && (
                      <RowPath>
                        <TextEllipsis
                          data={row[pathField]}
                          useMonospaceFont={true}
                          noTooltip={false}
                        />
                      </RowPath>
                    )}
                  </RowContainer>
                </td>
              </tr>
            ))}
          </MeasureTbody>
          {!isCalculating && (
            <tbody>
              {displayData.length === 0 ? (
                <tr>
                  <td className="empty-row">
                    No data available
                  </td>
                </tr>
              ) : (
                displayData.map((row, idx) => (
                  <TableRow
                    key={idx}
                    className={row.action_id ? 'clickable' : ''}
                    title={row.action_id ? 'Click to view action details' : ''}
                    onClick={() => handleRowClick(row)}
                    $urgencyColor={getUrgencyColor(row.urgency)}
                  >
                    <td>
                      <RowContainer>
                        <RowColumns>
                          {columns.map(col => (
                            <RowCell key={col.key} $width={getColumnWidth(col)}>
                              {col.render ? col.render(row[col.key], row) : row[col.key]}
                            </RowCell>
                          ))}
                        </RowColumns>
                        {activePathField && row[activePathField] && (
                          <RowPath>
                            <TextEllipsis
                              data={row[activePathField]}
                              useMonospaceFont={true}
                              noTooltip={true}
                            />
                          </RowPath>
                        )}
                      </RowContainer>
                    </td>
                  </TableRow>
                ))
              )}
            </tbody>
          )}
        </WidgetTable>
      </WidgetContent>
    </WidgetStyled>
  );
};

Widget.propTypes = {
  title: PropTypes.string.isRequired,
  columns: PropTypes.arrayOf(PropTypes.shape({
    key: PropTypes.string.isRequired,
    label: PropTypes.string.isRequired,
    width: PropTypes.number // Optional: percentage width (e.g., 25 for 25%). Columns without width share remaining space equally.
  })).isRequired,
  data: PropTypes.array.isRequired,
  loading: PropTypes.bool,
  pathField: PropTypes.string,
  pathField2: PropTypes.string, // Optional: second path field that alternates with pathField during page display
  onMaximize: PropTypes.func // Callback when maximize button is clicked, receives snapshot of data
};

Widget.defaultProps = {
  loading: false
};

const WidgetStyled = styled.div`
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  display: flex;
  flex-direction: column;
  overflow: hidden;
`;

const WidgetHeader = styled.div`
  background: #343a40;
  color: #fff;
  font-size: 1.4rem;
  font-weight: bold;
  padding: 7px 12px;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const MaximizeButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  color: #fff;
  font-size: 0.9rem;
  width: 24px;
  height: 24px;
  border-radius: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.4);
  }

  &:active {
    background: rgba(255, 255, 255, 0.5);
  }
`;

const PageIndicatorContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 4px;
`;

const PageIndicator = styled.span`
  font-size: 1rem;
  font-weight: normal;
  opacity: 0.8;
`;

const ProgressBar = styled.div`
  width: 120px;
  height: 4px;
  background: rgba(255, 255, 255, 0.3);
  border-radius: 2px;
  overflow: hidden;
`;

const ProgressFill = styled.div`
  height: 100%;
  width: ${props => props.$progress}%;
  background: rgba(255, 255, 255, 0.8);
  transition: width 0.5s linear;
`;

const WidgetContent = styled.div`
  flex: 1;
  overflow: hidden;
  position: relative;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1;

  i {
    font-size: 2rem;
    color: #343a40;
  }
`;

const WidgetTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  table-layout: fixed;

  thead {
    background: #f8f9fa;

    th {
      border-bottom: 2px solid #dee2e6;
      font-weight: bold;
      text-align: left;
      white-space: normal;
      word-wrap: break-word;
    }
  }

  tbody {
    tr {
      &:nth-child(even) {
        background: #f8f9fa;
      }

      &:not(:last-child) td {
        border-bottom: 1px solid #dee2e6;
      }

      &.clickable {
        cursor: pointer;

        &:hover {
          background: #e2e6ea;
        }
      }
    }

    td {
      padding: 0;
      white-space: normal;
      word-wrap: break-word;
      overflow-wrap: break-word;

      &.empty-row {
        text-align: center;
        color: #6c757d;
        padding: 24px;
      }
    }
  }
`;

const TableRow = styled.tr`
  ${props => props.$urgencyColor && `
    border-left: 3px solid ${props.$urgencyColor};
  `}
`;

const MeasureTbody = styled.tbody`
  visibility: ${props => props.$hidden ? 'hidden' : 'visible'};
  position: ${props => props.$hidden ? 'absolute' : 'static'};
  width: 100%;
`;

const RowContainer = styled.div`
  display: flex;
  flex-direction: column;
`;

const RowColumns = styled.div`
  display: flex;
  padding: 5px 0;
`;

const RowCell = styled.div`
  flex: 0 0 ${props => props.$width}%;
  width: ${props => props.$width}%;
  box-sizing: border-box;
  padding: 0 12px;
  white-space: normal;
  word-wrap: break-word;
  overflow-wrap: break-word;
`;

const RowPath = styled.div`
  padding: 0 12px 2px 12px;
  overflow: hidden;
  min-width: 0;
  width: 100%;
  white-space: nowrap;
  font-size: 0.85em;
  color: #6c757d;
`;

export default Widget;
